import React, { useState } from "react";
import ReactPlayer from "react-player";
import { FrameLabelPill, Icon, Spinner } from "../atoms";
import PlaybackControls from "./PlaybackControls";
import TimelineTrack from "./TimelineTrack";
import { PlayCard } from "./CurrentPlayPreview";

const MODES = [
  { id: "normal", label: "Normal", enabled: true, icon: "video-camera" },
  { id: "analisis", label: "Análisis", enabled: false, icon: "round-assessment" },
  { id: "auto", label: "Auto", enabled: false, icon: "baseline-hdr-auto" },
];

const OFFSETS = [-2, -1, 0, 1, 2];

const SIDE_PLACEMENT = {
  horizontal: "flex",
  vertical: "hidden",
};

const LEFT_COLUMNS = {
  horizontal: "lg:col-span-4 lg:col-start-1 lg:row-start-1",
  vertical: "",
};
const RIGHT_COLUMNS = {
  horizontal: "lg:col-span-4 lg:col-start-9 lg:row-start-1",
  vertical: "",
};
const CENTER_PLACEMENT = {
  horizontal: "lg:col-span-4 lg:col-start-5 lg:row-start-1",
  vertical: "self-start overflow-y-auto lg:col-span-6 lg:col-start-7 lg:row-start-1",
};

const VIDEO_OBJECT_FIT_STYLE = { objectFit: "contain", width: "100%", height: "100%" };
const VIDEO_CONFIG = { file: { attributes: { style: VIDEO_OBJECT_FIT_STYLE } } };

const frameValueFor = (currentFrame, offset, dynamicMaxFrames) => {
  const target = currentFrame + offset;
  if (offset <= 0) return Math.max(target, 0);
  return target < dynamicMaxFrames ? target : currentFrame;
};

const timeValueFor = (currentFrame, fps, offset, durationFrames) => {
  const target = Math.round(((currentFrame + offset) / fps) * 1000);
  if (offset <= 0) return Math.max(target, 0);
  return Math.min(target, Math.round((durationFrames / fps) * 1000));
};

function VideoThumb({
  offset,
  currentFrame,
  fps,
  videoDuration,
  loading,
  playing,
  url,
  frameLabel,
  timeMsLabel,
  onRef,
}) {
  const durationFrames = Math.round(videoDuration * fps);
  const rawMax = videoDuration * fps;
  const frame = frameValueFor(currentFrame, offset, rawMax);
  const timeMs = timeValueFor(currentFrame, fps, offset, durationFrames);

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-sm bg-base">
      <div className="flex h-[30px] w-3/5 min-w-[96px] flex-none flex-col justify-center self-start rounded-t-sm bg-control-bg px-1 py-0.5 text-[10px] leading-tight text-secondary">
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold whitespace-nowrap">{frameLabel}</span>
          <span className="font-medium text-primary">{frame}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold whitespace-nowrap">{timeMsLabel}</span>
          <span className="font-medium text-primary">{timeMs}</span>
        </div>
      </div>
      <div className="relative w-full aspect-video overflow-hidden rounded-b-sm rounded-tr-sm bg-black">
        <ReactPlayer
          ref={onRef}
          playing={playing}
          id="VideoPlayers"
          url={url}
          width="100%"
          height="100%"
          config={VIDEO_CONFIG}
          playdelay={offset < 0 ? offset * 33 : 0}
        />
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <Spinner size="sm" />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function VideoPlayerGroup({
  activeMode = "normal",
  onModeChange,
  videoRefs,
  playing,
  url,
  loading = false,
  currentFrame = 0,
  fps = 29.97,
  videoDuration = 0,
  currentTime = 0,
  onSeek,
  onProgress,
  onDuration,
  onEnded,
  onReady,
  controls,
  frameLabel: frameLabelOverride,
  timeMsLabel: timeMsLabelOverride,
  tabLabels,
  currentPlay = {},
  markActions,
  sessionActions,
  viewMode = "horizontal",
  className = "",
}) {
  const isVertical = viewMode === "vertical";
  const [internalMode, setInternalMode] = useState(activeMode);
  const currentMode = onModeChange ? activeMode : internalMode;

  const handleModeSelect = (modeId) => {
    if (!onModeChange) {
      setInternalMode(modeId);
    }
    if (onModeChange) {
      onModeChange(modeId);
    }
  };

  const labels = {
    frame: frameLabelOverride || "Frame",
    timeMs: timeMsLabelOverride || "Tiempo [ms]",
  };

  const modes = MODES.map((mode) => ({
    ...mode,
    label: (tabLabels && tabLabels[mode.id]) || mode.label,
  }));

  const onRef = (index) => (playerRef) => {
    if (!videoRefs) return;
    if (playerRef) videoRefs.current[index] = playerRef;
  };

  const durationFrames = Math.round(videoDuration * fps);
  const mainFrame = frameValueFor(currentFrame, 0, videoDuration * fps);
  const mainTimeMs = timeValueFor(currentFrame, fps, 0, durationFrames);

  return (
    <div className={`contents ${className}`}>
      <section
        className={`col-span-12 min-h-0 flex-col gap-2 ${SIDE_PLACEMENT[viewMode]} ${LEFT_COLUMNS[viewMode]}`}
      >
        <div className="mt-3 flex w-full flex-none gap-2">
          <VideoThumb
            offset={OFFSETS[0]}
            currentFrame={currentFrame}
            fps={fps}
            videoDuration={videoDuration}
            loading={loading}
            playing={playing}
            url={url}
            frameLabel={labels.frame}
            timeMsLabel={labels.timeMs}
            onRef={onRef(0)}
          />
          <VideoThumb
            offset={OFFSETS[1]}
            currentFrame={currentFrame}
            fps={fps}
            videoDuration={videoDuration}
            loading={loading}
            playing={playing}
            url={url}
            frameLabel={labels.frame}
            timeMsLabel={labels.timeMs}
            onRef={onRef(1)}
          />
        </div>
        <PlayCard
          title={currentPlay.prevTitle}
          image={currentPlay.prevImage}
          timeMs={currentPlay.prevTimeMs}
          playNumber={currentPlay.prevPlayNumber}
          timeMsLabel={currentPlay.timeMsLabel}
          playLabel={currentPlay.playLabel}
          play={currentPlay.prevPlay}
          imageClassName="object-contain shadow-md"
          className="min-h-0 flex-1 pt-2"
          tone="neutral"
        />
      </section>

      <section
        className={`col-span-12 min-h-0 w-full ${CENTER_PLACEMENT[viewMode]}`}
      >
        <div
          className={`box-border flex w-full min-h-0 ${isVertical ? "h-auto" : "h-full"} flex-col items-center gap-2 rounded-md bg-base p-2 shadow-md`}
        >
          {/* Video Header */}
          <div className="flex w-full flex-none items-end justify-between gap-2 border-b border-secondary-faded pb-1">
            {/* Tabs */}
            <div
              className="flex flex-none items-center gap-1"
              role="tablist"
              aria-label="Modos de visualización"
            >
              {modes.map((mode) => {
                const isActive = mode.id === currentMode;
                return (
                  <button
                    key={mode.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    disabled={!mode.enabled}
                    onClick={() => handleModeSelect(mode.id)}
                    className={`inline-flex items-center justify-center gap-1.5 bg-transparent px-2 py-1 text-xs capitalize transition-colors duration-150 border-0 border-b-2 -mb-[5px] cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-info-fg ${
                      isActive
                        ? "border-info-fg text-info-fg font-medium"
                        : "border-transparent text-secondary hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-secondary"
                    }`}
                  >
                    <Icon name={mode.icon} size="sm" />
                    <span>{mode.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Pills with auto-wrap responsive */}
            {!isVertical && (
              <div className="flex min-w-0 flex-wrap items-center justify-end gap-1.5">
                <FrameLabelPill label={labels.frame} value={mainFrame} />
                <FrameLabelPill label={labels.timeMs} value={mainTimeMs} />
              </div>
            )}
          </div>

          <div className="relative flex min-h-0 w-full flex-1 items-start justify-center overflow-hidden">
            <div
              className={`relative aspect-video overflow-hidden rounded-sm ${
                isVertical ? "w-full flex-none" : "h-full max-h-full max-w-full"
              }`}
            >
              <div className="absolute inset-0">
                <ReactPlayer
                ref={onRef(2)}
                playing={playing}
                id="VideoPlayers"
                url={url}
                width="100%"
                height="100%"
                config={VIDEO_CONFIG}
                playdelay={0}
                onDuration={(duration) => {
                  if (
                    typeof duration === "number" &&
                    Number.isFinite(duration) &&
                    duration > 0
                  ) {
                    onDuration && onDuration(duration);
                  }
                }}
                onProgress={(event) => {
                  if (event && onDuration) {
                    if (
                      event.played > 0 &&
                      event.playedSeconds > 0 &&
                      Number.isFinite(event.playedSeconds / event.played)
                    ) {
                      const calculatedDuration =
                        event.playedSeconds / event.played;
                      if (calculatedDuration > (videoDuration || 0)) {
                        onDuration(calculatedDuration);
                      }
                    } else if (event.playedSeconds > (videoDuration || 0)) {
                      onDuration(event.playedSeconds);
                    }
                  }
                  onProgress && onProgress(event);
                }}
                progressInterval={33}
                onEnded={onEnded}
                onReady={(playerRef) => {
                  if (playerRef && onDuration) {
                    try {
                      const d = playerRef.getDuration?.();
                      if (
                        typeof d === "number" &&
                        Number.isFinite(d) &&
                        d > 0
                      ) {
                        onDuration(d);
                      }
                    } catch (_) {}
                  }
                  onReady && onReady(playerRef);
                }}
              />
            </div>
            {loading ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <Spinner size="md" />
              </div>
            ) : null}
          </div>
        </div>

          <TimelineTrack
            className="w-full flex-none"
            currentTime={currentTime}
            duration={Math.max(videoDuration || 0, currentTime || 0)}
            fps={fps}
            onSeek={onSeek}
          />

          <div className="flex w-full flex-none justify-center">
            {controls || <PlaybackControls onPlayPauseToggle={() => {}} className="!py-1" />}
          </div>

          {(markActions || sessionActions) && (
            <div className="box-border flex w-full flex-none flex-col gap-1.5 rounded-sm bg-surface-faded p-1.5 shadow-sm">
              {markActions ? (
                <div className="flex w-full items-center gap-2">
                  {markActions}
                </div>
              ) : null}
              {sessionActions ? (
                <div className="flex w-full items-center gap-2">
                  {sessionActions}
                </div>
              ) : null}
            </div>
          )}
        </div>
      </section>

      <section
        className={`col-span-12 min-h-0 flex-col gap-2 ${SIDE_PLACEMENT[viewMode]} ${RIGHT_COLUMNS[viewMode]}`}
      >
        <div className="mt-3 flex w-full flex-none gap-2">
          <VideoThumb
            offset={OFFSETS[3]}
            currentFrame={currentFrame}
            fps={fps}
            videoDuration={videoDuration}
            loading={loading}
            playing={playing}
            url={url}
            frameLabel={labels.frame}
            timeMsLabel={labels.timeMs}
            onRef={onRef(3)}
          />
          <VideoThumb
            offset={OFFSETS[4]}
            currentFrame={currentFrame}
            fps={fps}
            videoDuration={videoDuration}
            loading={loading}
            playing={playing}
            url={url}
            frameLabel={labels.frame}
            timeMsLabel={labels.timeMs}
            onRef={onRef(4)}
          />
        </div>
        <PlayCard
          title={currentPlay.currentTitle}
          image={currentPlay.currentImage}
          timeMs={currentPlay.currentTimeMs}
          playNumber={currentPlay.currentPlayNumber}
          timeMsLabel={currentPlay.timeMsLabel}
          playLabel={currentPlay.playLabel}
          play={currentPlay.currentPlay}
          imageClassName="object-contain shadow-md"
          className="min-h-0 flex-1 pt-2"
          tone="info"
        />
      </section>
    </div>
  );
}

export default VideoPlayerGroup;